import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API_BASE, BUSINESS } from "@/lib/config";

const MATERIAL_OPTIONS = [
  "Steel",
  "Aluminum",
  "Stainless Steel",
  "Carbon Steel",
  "Anodized Aluminum",
  "Wood",
  "Acrylic",
  "Leather",
  "Other / Not Sure",
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  material: "",
  quantity: "",
  project_details: "",
};

export default function BookCall() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.project_details) {
      toast.error("Please fill out name, email, phone, and project details.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/leads`, {
        ...form,
        material: form.material || null,
        quantity: form.quantity || null,
      });
      toast.success("Got it. We'll reach out within 1 business day.");
      setForm(initialForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please call us directly.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      className="relative border-b border-zinc-800 bg-zinc-950 py-20 sm:py-28"
      data-testid="book-section"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14">
          <p className="font-mono-spec mb-3 text-orange-500">// 04 — Get a Quote</p>
          <h2 className="font-display text-5xl text-white sm:text-6xl lg:text-7xl">
            Two Ways to Start.
            <br />
            <span className="text-zinc-500">Both Take 60 Seconds.</span>
          </h2>
        </div>

        <div className="grid border border-zinc-800 lg:grid-cols-2">
          {/* LEFT — Calendly / Call */}
          <div className="border-b border-zinc-800 bg-zinc-900/40 p-8 sm:p-10 lg:border-b-0 lg:border-r">
            <p className="font-mono-spec text-zinc-500">Option A</p>
            <h3 className="mt-2 font-display text-4xl text-white sm:text-5xl">
              Book a Call
            </h3>
            <p className="mt-4 text-zinc-400">
              Talk to the person actually running the shop. 15-minute call,
              walk through your project, get a verbal quote on the spot.
            </p>

            <a
              href={BUSINESS.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shadow-brutal mt-8 inline-flex h-16 w-full items-center justify-center gap-3 border-2 border-orange-600 bg-orange-600 px-6 font-display text-2xl uppercase tracking-wider text-white sm:w-auto"
              data-testid="book-calendly-btn"
            >
              <Calendar className="h-6 w-6" />
              Schedule on Calendly
            </a>

            <div className="mt-10 space-y-4 border-t border-zinc-800 pt-8">
              <div>
                <p className="font-mono-spec text-zinc-500">Prefer to call?</p>
                <a
                  href={BUSINESS.phoneTel}
                  className="mt-1 block font-display text-3xl text-white hover:text-orange-500 sm:text-4xl"
                  data-testid="book-phone-link"
                >
                  {BUSINESS.phone}
                </a>
              </div>
              <div>
                <p className="font-mono-spec text-zinc-500">Or email us</p>
                <a
                  href={BUSINESS.emailMailto}
                  className="mt-1 block break-all font-display text-xl text-white hover:text-orange-500 sm:text-2xl"
                  data-testid="book-email-link"
                >
                  {BUSINESS.email}
                </a>
              </div>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div className="bg-zinc-950 p-8 sm:p-10">
            <p className="font-mono-spec text-zinc-500">Option B</p>
            <h3 className="mt-2 font-display text-4xl text-white sm:text-5xl">
              Send a Quote Request
            </h3>
            <p className="mt-4 text-zinc-400">
              Fill it out, we&apos;ll get back within 1 business day with pricing
              and lead time.
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-8 space-y-5"
              data-testid="lead-form"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Name *" htmlFor="name">
                  <Input
                    id="name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Smith"
                    className="h-12 rounded-none border-2 border-zinc-800 bg-zinc-950 text-white placeholder:text-zinc-600 focus-visible:border-orange-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="lead-name-input"
                  />
                </Field>
                <Field label="Email *" htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@company.com"
                    className="h-12 rounded-none border-2 border-zinc-800 bg-zinc-950 text-white placeholder:text-zinc-600 focus-visible:border-orange-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="lead-email-input"
                  />
                </Field>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Phone *" htmlFor="phone">
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(555) 123-4567"
                    className="h-12 rounded-none border-2 border-zinc-800 bg-zinc-950 text-white placeholder:text-zinc-600 focus-visible:border-orange-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="lead-phone-input"
                  />
                </Field>
                <Field label="Quantity" htmlFor="quantity">
                  <Input
                    id="quantity"
                    value={form.quantity}
                    onChange={update("quantity")}
                    placeholder="e.g. 50 pieces"
                    className="h-12 rounded-none border-2 border-zinc-800 bg-zinc-950 text-white placeholder:text-zinc-600 focus-visible:border-orange-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="lead-quantity-input"
                  />
                </Field>
              </div>

              <Field label="Material" htmlFor="material">
                <Select
                  value={form.material}
                  onValueChange={(v) => setForm((f) => ({ ...f, material: v }))}
                >
                  <SelectTrigger
                    id="material"
                    className="h-12 rounded-none border-2 border-zinc-800 bg-zinc-950 text-white focus:border-orange-600 focus:ring-0 focus:ring-offset-0 data-[placeholder]:text-zinc-600"
                    data-testid="lead-material-select"
                  >
                    <SelectValue placeholder="Select a material" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-2 border-zinc-800 bg-zinc-950 text-white">
                    {MATERIAL_OPTIONS.map((m) => (
                      <SelectItem
                        key={m}
                        value={m}
                        className="rounded-none focus:bg-orange-600 focus:text-white"
                        data-testid={`material-option-${m.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Project Details *" htmlFor="project_details">
                <Textarea
                  id="project_details"
                  value={form.project_details}
                  onChange={update("project_details")}
                  rows={5}
                  placeholder="What are you making? Dimensions, finish, deadline, anything useful."
                  className="rounded-none border-2 border-zinc-800 bg-zinc-950 text-white placeholder:text-zinc-600 focus-visible:border-orange-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="lead-details-input"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="shadow-brutal mt-2 inline-flex h-16 w-full items-center justify-center gap-3 border-2 border-orange-600 bg-orange-600 px-8 font-display text-2xl uppercase tracking-wider text-white disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                data-testid="lead-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Send Quote Request
                  </>
                )}
              </button>
              <p className="font-mono-spec text-zinc-500">
                We reply within 1 business day. Usually faster.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={htmlFor}
        className="font-mono-spec text-zinc-400"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
